/**
 * Gamificación de **famidex** — capa de dominio (específica de la app).
 *
 * El núcleo (`engine.ts`) es agnóstico; aquí se define QUÉ eventos de famidex
 * dan recompensas y cómo se derivan del sustrato `activity` (oplog local-first,
 * ~90 días en el bucket PowerSync `activity_recent`). Al derivar el
 * `PlayerState` del feed ya existente NO hace falta una columna de sync nueva:
 * el reducer es puro y determinista, se recomputa en el cliente.
 *
 * Gamificación **positiva y opcional**: XP por completar tareas, racha suave de
 * "días productivos", badges de constancia. Nada punitivo; ligas privadas por
 * defecto (el core solo rankea opt-in).
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState } from "./engine";
import { computeStreak, dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Línea de racha de famidex: "días con al menos una tarea completada". */
export const STREAK_PRODUCTIVITY = "productivity";

/**
 * Clave semántica de un evento de dominio a partir de la fila de activity.
 * Reimplementa (puro, sin i18n) el refinamiento de `activitySemanticKey` de la
 * app: distingue completar/reabrir/papelera mirando el patch.
 */
function semanticKey(row: Pick<ActivityRow, "entity_type" | "action" | "patch">): string {
  const action = row.action ?? "updated";
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "todo" && action === "updated" && patch.completed) {
    return patch.completed.new ? "completed" : "reopened";
  }
  if (action === "deleted") {
    return patch.deleted_at ? "trashed" : "purged";
  }
  return action;
}

/**
 * Deriva el `GameEvent` de una fila de activity, o `null` si no otorga nada.
 * El `type` es `"{entity_type}.{semanticKey}"` (p. ej. `"todo.completed"`).
 * El `day` (para rachas) sale de `client_ts` (o `server_ts`) en UTC.
 */
export function activityRowToGameEvent(row: ActivityRow): GameEvent | null {
  if (!row.entity_type) {
    return null;
  }
  const key = semanticKey(row);
  const type = `${row.entity_type}.${key}`;
  const stamp = row.client_ts ?? row.server_ts;
  const at = stamp ? Date.parse(stamp) : Number.NaN;
  if (Number.isNaN(at)) {
    return null;
  }
  return { type, at, day: dayKeyUTC(at) };
}

/** Filas de activity (feed ya cargado local-first) → eventos de juego en orden. */
export function activityRowsToGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  // El feed llega server_ts DESC; el reducer es conmutativo para XP/contadores
  // pero las rachas se recomputan sobre el set de días, así que el orden es
  // irrelevante. Aun así lo emitimos en orden cronológico ascendente.
  for (const row of rows.toReversed()) {
    const ev = activityRowToGameEvent(row);
    if (ev) {
      events.push(ev);
    }
  }
  return events;
}

/**
 * `GamificationConfig` de famidex. Solo eventos "positivos" dan XP; reabrir,
 * editar o borrar no penalizan (0 XP). Racha suave: completar una tarea marca
 * el día como productivo.
 */
export const FAMIDEX_GAMIFICATION: GamificationConfig = {
  xp: {
    "todo.completed": 10,
    "todo.created": 2,
    "subtask.completed": 3,
    "list.created": 5,
  },
  streakLines: {
    "todo.completed": [STREAK_PRODUCTIVITY],
  },
  counters: {
    "todo.completed": ["tasks_done"],
    "todo.created": ["tasks_created"],
    "list.created": ["lists_created"],
  },
  badges: [
    { id: "first-task", title: "Primer paso", when: counterAtLeast("tasks_done", 1) },
    { id: "ten-tasks", title: "En marcha", when: counterAtLeast("tasks_done", 10) },
    { id: "fifty-tasks", title: "Imparable", when: counterAtLeast("tasks_done", 50), bonusXp: 25 },
    {
      id: "century",
      title: "Centenario",
      when: counterAtLeast("tasks_done", 100),
      bonusXp: 50,
    },
    {
      id: "week-streak",
      title: "Semana productiva",
      when: streakAtLeast(STREAK_PRODUCTIVITY, 7),
      bonusXp: 40,
    },
    {
      id: "month-streak",
      title: "Mes en racha",
      when: streakAtLeast(STREAK_PRODUCTIVITY, 30),
      bonusXp: 100,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-3",
      title: "3 tareas hoy",
      period: "daily",
      goals: [{ metric: "todo.completed", target: 3 }],
      bonusXp: 15,
    },
    {
      id: "weekly-15",
      title: "15 tareas esta semana",
      period: "weekly",
      goals: [{ metric: "todo.completed", target: 15 }],
      bonusXp: 40,
    },
  ],
};

/** Catálogo de badges (para pintar los aún no obtenidos, en gris). */
export const FAMIDEX_BADGES = FAMIDEX_GAMIFICATION.badges ?? [];

// ── Derivación LOCAL-FIRST desde la tabla `todo` ───────────────────────────
//
// `activity_recent` es un bucket PROYECTADO por el servidor (read-only, llega
// por sync), así que no da feedback instantáneo ni existe para invitados. La
// tabla `todo` sí es local-first viva: al completar una tarea el XP sube al
// instante. Esta es la fuente de la UI; el mapeo desde `activity` se conserva
// para el historial/antifraude en servidor.

/** Fila mínima de `todo` que la gamificación necesita (subconjunto del schema). */
export interface GamifiableTodo {
  completed: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Deriva eventos de juego de las filas `todo` vivas (no borradas). Cada tarea
 * emite `todo.created` (por `created_at`) y, si está completada, `todo.completed`
 * (día ≈ `updated_at`, la última vez que se tocó). Puro y determinista.
 */
export function todoRowsToGameEvents(todos: readonly GamifiableTodo[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const t of todos) {
    if (t.created_at) {
      const at = Date.parse(t.created_at);
      if (!Number.isNaN(at)) {
        events.push({ type: "todo.created", at, day: dayKeyUTC(at) });
      }
    }
    if (t.completed) {
      const stamp = t.updated_at ?? t.created_at;
      const at = stamp ? Date.parse(stamp) : Number.NaN;
      if (!Number.isNaN(at)) {
        events.push({ type: "todo.completed", at, day: dayKeyUTC(at) });
      }
    }
  }
  // Orden cronológico ascendente (irrelevante para XP/contadores; ordena rachas).
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recompute del estado del jugador desde la tabla `todo` (local-first, feedback
 * instantáneo). Reancla la racha de productividad a "hoy" real. Puro.
 */
export function computePlayerFromTodos(
  todos: readonly GamifiableTodo[],
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = todoRowsToGameEvents(todos);
  const { state } = applyEvents(initialState(), events, FAMIDEX_GAMIFICATION);
  const active = state.activeDays[STREAK_PRODUCTIVITY] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_PRODUCTIVITY] = streak;
  }
  return { state, events: events.length };
}

/**
 * Recompute completo del estado del jugador a partir del feed de activity.
 * Puro y determinista. `todayKey` permite fijar "hoy" (tests / zona horaria);
 * por defecto se usa la fecha UTC actual.
 */
export function computePlayerFromActivity(
  rows: readonly ActivityRow[],
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = activityRowsToGameEvents(rows);
  const { state } = applyEvents(initialState(), events, FAMIDEX_GAMIFICATION);
  // El motor ancla la racha al último día activo (para no romper "hoy sin
  // marcar"). Para la UI reanclamos a "hoy" real: si el último día productivo
  // quedó lejos, la racha actual muere (best se conserva).
  const active = state.activeDays[STREAK_PRODUCTIVITY] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_PRODUCTIVITY] = streak;
  }
  return { state, events: events.length };
}

// ── FAMIDEX: gamificación desde la COLECCIÓN (modelo de dominio REAL) ────────
//
// El modelo vivo de famidex NO es `todo` sino `user_collection` (local-first,
// guest-first, gotcha #8). El perfil deriva el estado del jugador de las
// ADQUISICIONES: añadir un producto en estado `owned`/`duplicate` estampa
// +50 XP (matriz de la ficha), añadir a la lista de deseos (`wishlist`) +5.
// Racha "coleccionista" = días con al menos una adquisición. Puro y
// determinista → feedback instantáneo, sin columna de sync nueva y sin depender
// de `activity_recent` (que es una proyección read-only del servidor).

/** Línea de racha de famidex: "días con al menos una adquisición". */
export const STREAK_COLLECTING = "collecting";

/**
 * `GamificationConfig` de famidex sobre la colección. Solo eventos "positivos"
 * dan XP; la lista de deseos suma poco (intención, no posesión). Badges e hitos
 * reencuadrados al dominio de coleccionar (no a completar tareas).
 */
export const FAMIDEX_COLLECTION_GAMIFICATION: GamificationConfig = {
  xp: {
    "collection.owned": 50,
    "collection.wishlist": 5,
  },
  streakLines: {
    "collection.owned": [STREAK_COLLECTING],
  },
  counters: {
    "collection.owned": ["items_collected"],
    "collection.wishlist": ["items_wished"],
  },
  badges: [
    { id: "first-find", title: "Primer hallazgo", when: counterAtLeast("items_collected", 1) },
    { id: "ten-finds", title: "Coleccionista", when: counterAtLeast("items_collected", 10) },
    {
      id: "fifty-finds",
      title: "Curador",
      when: counterAtLeast("items_collected", 50),
      bonusXp: 25,
    },
    {
      id: "century-finds",
      title: "Gran conservador",
      when: counterAtLeast("items_collected", 100),
      bonusXp: 50,
    },
    {
      id: "week-collector",
      title: "Semana coleccionista",
      when: streakAtLeast(STREAK_COLLECTING, 7),
      bonusXp: 40,
    },
    {
      id: "month-collector",
      title: "Mes coleccionista",
      when: streakAtLeast(STREAK_COLLECTING, 30),
      bonusXp: 100,
    },
    { id: "collector-level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "collector-level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
};

/** Catálogo de badges de colección (para pintar los aún no obtenidos, en gris). */
export const FAMIDEX_COLLECTION_BADGES = FAMIDEX_COLLECTION_GAMIFICATION.badges ?? [];

/** Fila mínima de `user_collection` que la gamificación necesita. */
export interface GamifiableCollectionItem {
  /** `owned` | `wishlist` | `duplicate` (o null ≈ owned). */
  state: string | null;
  acquired_at: string | null;
  updated_at: string | null;
}

/**
 * Deriva eventos de juego de las filas de colección vivas (no borradas). Cada
 * ítem emite un evento en su día de adquisición (`acquired_at`, o `updated_at`
 * como respaldo). `wishlist` → `collection.wishlist` (+5); todo lo demás
 * (`owned`, `duplicate`, null) → `collection.owned` (+50). Puro y determinista.
 */
export function collectionRowsToGameEvents(
  items: readonly GamifiableCollectionItem[],
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const it of items) {
    const stamp = it.acquired_at ?? it.updated_at;
    const at = stamp ? Date.parse(stamp) : Number.NaN;
    if (Number.isNaN(at)) {
      continue;
    }
    const type = it.state === "wishlist" ? "collection.wishlist" : "collection.owned";
    events.push({ type, at, day: dayKeyUTC(at) });
  }
  // Orden cronológico ascendente (irrelevante para XP/contadores; ordena rachas).
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recompute del estado del jugador desde la colección local-first (feedback
 * instantáneo). Reancla la racha coleccionista a "hoy" real. Puro.
 */
export function computePlayerFromCollection(
  items: readonly GamifiableCollectionItem[],
  todayKey: string = dayKeyUTC(Date.now()),
): { state: PlayerState; events: number } {
  const events = collectionRowsToGameEvents(items);
  const { state } = applyEvents(initialState(), events, FAMIDEX_COLLECTION_GAMIFICATION);
  const active = state.activeDays[STREAK_COLLECTING] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_COLLECTING] = streak;
  }
  return { state, events: events.length };
}
